import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { ArrowRight, Sparkles, Target, Heart, Users, TrendingUp, Zap } from 'lucide-react';
import CalendlyPopup from '../components/CalendlyPopup';
import { useCalendlyAndVideo } from '../hooks/useCalendlyAndVideo';

const AboutPage = () => {
  const { isCalendlyOpen, openCalendly, closeCalendly } = useCalendlyAndVideo();

  const navigatorCapabilities = [
    { id: 'knowledge', text: 'Turn existing knowledge into structured, usable learning' },
    { id: 'practice', text: 'Replace passive training with real practice and feedback' },
    { id: 'consistency', text: 'Create consistency without stripping away humanity' },
    { id: 'scale-dev', text: 'Scale development without overwhelming managers' }
  ];

  const visionPoints = [
    { id: 'evolve', text: 'Training evolves as fast as the business' },
    { id: 'prepared', text: 'Employees feel prepared, not pressured' },
    { id: 'clarity', text: 'Leaders gain clarity instead of chaos' },
    { id: 'growth', text: 'Growth doesn\'t come at the cost of burnout' }
  ];

  const missionPoints = [
    { id: 'modernize', text: 'Modernizing training without discarding what already works' },
    { id: 'empower', text: 'Empowering HR and leadership teams with intelligent tools' },
    { id: 'ai', text: 'Using AI to accelerate learning while honoring the human experience' },
    { id: 'scale', text: 'Creating systems that scale people, not just processes' }
  ];

  const outcomes = [
    { id: 'ramp', icon: Zap, text: 'People ramp faster' },
    { id: 'perform', icon: TrendingUp, text: 'Teams perform better' },
    { id: 'culture', icon: Users, text: 'Leaders spend less time fixing problems and more time building culture' }
  ];

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <SEO 
        title="About Traain | Our Story"
        description="Traain exists because work has changed—but the systems designed to support people haven't. Learn about our mission to transform workforce training."
        keywords="about traain, training platform, workforce development, AI training, Navigator AI"
      />
      
      {/* Animated Background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-950 via-gray-900 to-indigo-950" />
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-96 h-96 bg-cyan-500 rounded-full filter blur-3xl animate-blob" />
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl animate-blob animation-delay-2000" />
          <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-indigo-500 rounded-full filter blur-3xl animate-blob animation-delay-4000" />
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10">
        <Navbar />
        
        {/* Hero Section */}
        <section className="pt-32 pb-20 px-6">
          <div className="max-w-5xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-8 text-white">
              About <span className="text-cyan-400">Traain</span>
            </h1>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              The Workforce Is More Fragmented Than Ever
            </h2>
            
            <p className="text-2xl md:text-3xl text-cyan-400 font-semibold mb-12">
              And the People Responsible for It Are Carrying the Weight.
            </p>
          </div>
        </section>

        {/* The Problem Section */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Today's workplaces are faster, more distributed, and more complex than anything we've seen before.
            </p>
            
            <div className="space-y-4 mb-10">
              {[
                { id: 'spread', text: 'Teams are spread across locations, time zones, and roles.' },
                { id: 'tenure', text: 'Tenure is shorter. Expectations are higher.' },
                { id: 'managers', text: 'Managers are stretched thin.' },
                { id: 'hr', text: 'HR leaders are asked to do more—with less time, fewer resources, and constant pressure to prove impact.' }
              ].map((point) => (
                <div key={point.id} className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-cyan-400 rounded-full mt-3 flex-shrink-0" />
                  <p className="text-lg text-gray-300">{point.text}</p>
                </div>
              ))}
            </div>

            <div className="p-8 rounded-2xl bg-gradient-to-br from-red-900/20 to-orange-900/20 border border-red-500/30 mb-10">
              <p className="text-2xl font-bold text-white mb-4">Training hasn't kept up.</p>
              <p className="text-lg text-gray-300">
                Most organizations are still relying on static LMS platforms, outdated content, and one-size-fits-all programs—while hoping people somehow turn that into real performance.
              </p>
            </div>

            <p className="text-xl text-gray-300 mb-4 leading-relaxed">
              That gap is where friction lives.
            </p>
            <p className="text-xl text-gray-300 leading-relaxed">
              And that friction shows up as slow ramp times, inconsistent performance, burnout, and disengagement.
            </p>
          </div>
        </section>

        {/* Built for People Section */}
        <section className="py-20 px-6 bg-gradient-to-b from-transparent to-blue-950/20">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 text-center">
              We Built Traain for the People in the Middle of <span className="text-cyan-400">That Pressure</span>
            </h2>
            
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Traain exists because work has changed—but the systems designed to support people haven't.
            </p>
            
            <div className="space-y-4 mb-10">
              <p className="text-lg text-gray-300">We believe training should <span className="text-white font-semibold">enable people</span>, not slow them down.</p>
              <p className="text-lg text-gray-300">That it should <span className="text-white font-semibold">adapt to how teams actually work today</span>.</p>
              <p className="text-lg text-gray-300">And that HR and talent leaders deserve tools that <span className="text-white font-semibold">respect both human complexity and business reality</span>.</p>
            </div>

            <div className="p-8 rounded-2xl bg-gradient-to-br from-gray-900/90 to-blue-900/30 border border-cyan-500/30">
              <div className="space-y-3 mb-6">
                <p className="text-lg text-gray-300">Yes—<span className="text-cyan-400 font-semibold">performance</span> matters.</p>
                <p className="text-lg text-gray-300">Yes—<span className="text-cyan-400 font-semibold">ramp time</span> matters.</p>
                <p className="text-lg text-gray-300">Yes—<span className="text-cyan-400 font-semibold">outcomes</span> matter.</p>
              </div>
              <p className="text-xl text-white font-semibold">
                But at the center of every metric is a person trying to do good work.
              </p>
            </div>
          </div>
        </section>

        {/* AI Section */}
        <section className="py-20 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 text-center">
              AI Isn't Here to Replace People
            </h2>
            <p className="text-2xl text-cyan-400 font-semibold mb-12 text-center">
              It's Here to Give Them a Better System
            </p>
            
            <p className="text-xl text-gray-300 mb-4 leading-relaxed">
              At Traain, we're in the people business.
            </p>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed">
              We just happen to have <span className="text-cyan-400 font-semibold">Navigator AI</span> in our back pocket.
            </p>

            <div className="p-8 rounded-2xl bg-gradient-to-br from-cyan-900/20 to-blue-900/30 border border-cyan-500/30 mb-10">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_f6991d0e-ab71-4b45-956d-bb71024e7667/artifacts/p0nzve01_Naviagtor%20AI.png" 
                    alt="Navigator AI"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-2xl font-bold text-white">Navigator AI allows organizations to:</h3>
              </div>
              <div className="space-y-4">
                {navigatorCapabilities.map((capability) => (
                  <div key={capability.id} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 flex-shrink-0" />
                    <p className="text-lg text-gray-300">{capability.text}</p>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-lg text-gray-300 mb-4 leading-relaxed">
              AI does the heavy lifting—organizing, adapting, accelerating—
            </p>
            <p className="text-xl text-white font-semibold leading-relaxed">
              So leaders can focus on what only humans can do: coach, connect, and lead.
            </p>
          </div>
        </section>

        {/* Vision Section */}
        <section className="py-20 px-6 bg-gradient-to-b from-transparent to-blue-950/20">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center gap-4 mb-8">
              <Target className="w-10 h-10 text-cyan-400" />
              <h2 className="text-3xl md:text-4xl font-bold text-white">Our Vision</h2>
            </div>
            
            <p className="text-xl text-gray-300 mb-10 text-center leading-relaxed">
              A world where work is more human, more effective, and more aligned—because people are supported by systems that actually work.
            </p>

            <div className="p-8 rounded-2xl bg-gradient-to-br from-gray-900/90 to-blue-900/30 border border-cyan-500/30">
              <h3 className="text-xl font-bold text-white mb-6">We envision workplaces where:</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {visionPoints.map((point) => (
                  <div key={point.id} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 flex-shrink-0" />
                    <p className="text-gray-300">{point.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-20 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center gap-4 mb-8">
              <Heart className="w-10 h-10 text-cyan-400" />
              <h2 className="text-3xl md:text-4xl font-bold text-white">Our Mission</h2>
            </div>
            
            <p className="text-xl text-gray-300 mb-10 text-center leading-relaxed">
              To help organizations eliminate workforce friction and unlock performance—by transforming how people learn, practice, and grow.
            </p>

            <div className="p-8 rounded-2xl bg-gradient-to-br from-gray-900/90 to-blue-900/30 border border-cyan-500/30">
              <h3 className="text-xl font-bold text-white mb-6">We do this by:</h3>
              <div className="space-y-4">
                {missionPoints.map((point) => (
                  <div key={point.id} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 flex-shrink-0" />
                    <p className="text-gray-300">{point.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Why It Matters Section */}
        <section className="py-20 px-6 bg-gradient-to-b from-transparent to-blue-950/20">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
              Why It <span className="text-cyan-400">Matters</span>
            </h2>
            
            <p className="text-2xl text-white font-bold mb-10">
              Because talent doesn't fail—systems do.
            </p>

            <p className="text-xl text-gray-300 mb-10">And when the system works:</p>

            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {outcomes.map((outcome) => {
                const Icon = outcome.icon;
                return (
                  <div
                    key={outcome.id}
                    className="p-6 rounded-2xl bg-gradient-to-br from-gray-900/90 to-blue-900/30 border border-cyan-500/30"
                  >
                    <Icon className="w-10 h-10 text-cyan-400 mx-auto mb-4" />
                    <p className="text-gray-300">{outcome.text}</p>
                  </div>
                );
              })}
            </div>

            <p className="text-xl text-gray-300 mb-2">That's not just good for business.</p>
            <p className="text-xl text-white font-semibold mb-8">That's good for people.</p>
            
            <p className="text-2xl text-cyan-400 font-bold mb-4">And that's why Traain exists.</p>
            <p className="text-3xl md:text-4xl font-bold text-white">All Aboard!</p>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Button
              size="lg"
              onClick={openCalendly}
              className="h-14 bg-cyan-500 text-black hover:bg-cyan-400 rounded-lg px-8 text-lg font-semibold transition-all duration-200 hover:scale-105 hover:shadow-lg hover:shadow-cyan-500/50"
            >
              Schedule a Discovery Call
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </section>

        <Footer />
      </div>
      
      <CalendlyPopup isOpen={isCalendlyOpen} onClose={closeCalendly} />
    </div>
  );
};

export default AboutPage;
